'use client'

import { useEffect, useRef, type ReactNode } from 'react'

/**
 * The depth of the home hero's map (ALU-274): writes where the pointer is
 * (`--hm-x`, `--hm-y`, -1 to 1 across the window) and where the map is in the
 * viewport (`--hm-s`, -1 to 1 as it scrolls through) on this element, and
 * the layers under it move a few pixels each by those, the base least and
 * the cards most (hero-visual.tsx).
 *
 * Restrained on purpose: a handful of pixels, eased, and nothing at all when
 * the reader asks for reduced motion. The pointer only counts with a fine
 * pointer that hovers (a mouse or a trackpad), never on touch.
 */
export function HeroMapMotion({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    // No matchMedia (an old engine, a test DOM): the map just stays still.
    if (!el || typeof window.matchMedia !== 'function') return
    const motion = window.matchMedia('(prefers-reduced-motion: no-preference)')
    const pointer = window.matchMedia('(hover: hover) and (pointer: fine)')
    const target = { x: 0, y: 0 }
    const now = { x: 0, y: 0 }
    let frame = 0

    const paint = () => {
      frame = 0
      if (!motion.matches) {
        now.x = now.y = target.x = target.y = 0
        el.style.removeProperty('--hm-x')
        el.style.removeProperty('--hm-y')
        el.style.removeProperty('--hm-s')
        return
      }
      now.x += (target.x - now.x) * 0.12
      now.y += (target.y - now.y) * 0.12
      const box = el.getBoundingClientRect()
      const middle = box.top + box.height / 2 - window.innerHeight / 2
      const scroll = Math.max(-1, Math.min(1, middle / window.innerHeight))
      el.style.setProperty('--hm-x', now.x.toFixed(3))
      el.style.setProperty('--hm-y', now.y.toFixed(3))
      el.style.setProperty('--hm-s', scroll.toFixed(3))
      if (Math.abs(target.x - now.x) > 0.002 || Math.abs(target.y - now.y) > 0.002) schedule()
    }
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(paint)
    }
    const onMove = (event: PointerEvent) => {
      if (!pointer.matches || event.pointerType !== 'mouse') return
      target.x = (event.clientX / window.innerWidth) * 2 - 1
      target.y = (event.clientY / window.innerHeight) * 2 - 1
      schedule()
    }
    const onLeave = () => {
      target.x = target.y = 0
      schedule()
    }

    window.addEventListener('pointermove', onMove, { passive: true })
    document.documentElement.addEventListener('pointerleave', onLeave)
    window.addEventListener('scroll', schedule, { passive: true })
    motion.addEventListener('change', schedule)
    schedule()
    return () => {
      window.removeEventListener('pointermove', onMove)
      document.documentElement.removeEventListener('pointerleave', onLeave)
      window.removeEventListener('scroll', schedule)
      motion.removeEventListener('change', schedule)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [])

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  )
}
